import { PDFDocument, rgb, StandardFonts, degrees, PageSizes } from 'pdf-lib';

export interface MergeOptions {
  files: ArrayBuffer[];
  pageRanges?: { fileIndex: number; start: number; end: number }[];
}

export interface SplitOptions {
  source: ArrayBuffer;
  ranges: { start: number; end: number }[];
}

export interface AddTextOptions {
  x: number;
  y: number;
  text: string;
  fontSize?: number;
  color?: { r: number; g: number; b: number };
  fontFamily?: string;
}

export interface AddImageOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  imageBytes: ArrayBuffer;
  imageType: 'png' | 'jpg';
}

export class PDFManipulator {
  private static async loadPdf(source: ArrayBuffer): Promise<PDFDocument> {
    const copy = source.slice(0);
    return PDFDocument.load(copy, { ignoreEncryption: true });
  }

  /**
   * Merge multiple PDF files into one
   */
  static async merge(options: MergeOptions): Promise<Uint8Array> {
    const mergedPdf = await PDFDocument.create();

    for (let i = 0; i < options.files.length; i++) {
      const sourcePdf = await PDFManipulator.loadPdf(options.files[i]);
      const pageCount = sourcePdf.getPageCount();

      let pagesToCopy: number[];

      if (options.pageRanges) {
        const range = options.pageRanges.find((r) => r.fileIndex === i);
        if (range) {
          pagesToCopy = Array.from(
            { length: range.end - range.start + 1 },
            (_, j) => range.start - 1 + j
          );
        } else {
          pagesToCopy = Array.from({ length: pageCount }, (_, j) => j);
        }
      } else {
        pagesToCopy = Array.from({ length: pageCount }, (_, j) => j);
      }

      const copiedPages = await mergedPdf.copyPages(sourcePdf, pagesToCopy);
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }

    return mergedPdf.save();
  }

  /**
   * Split a PDF into multiple documents by page ranges
   */
  static async split(options: SplitOptions): Promise<Uint8Array[]> {
    const sourcePdf = await PDFManipulator.loadPdf(options.source);
    const results: Uint8Array[] = [];

    for (const range of options.ranges) {
      const newPdf = await PDFDocument.create();
      const pageIndices = Array.from(
        { length: range.end - range.start + 1 },
        (_, i) => range.start - 1 + i
      );

      const copiedPages = await newPdf.copyPages(sourcePdf, pageIndices);
      copiedPages.forEach((page) => newPdf.addPage(page));
      results.push(await newPdf.save());
    }

    return results;
  }

  /**
   * Extract specific pages from a PDF
   */
  static async extractPages(source: ArrayBuffer, pageNumbers: number[]): Promise<Uint8Array> {
    const sourcePdf = await PDFManipulator.loadPdf(source);
    const newPdf = await PDFDocument.create();
    const indices = pageNumbers.map((n) => n - 1);
    const copiedPages = await newPdf.copyPages(sourcePdf, indices);
    copiedPages.forEach((page) => newPdf.addPage(page));
    return newPdf.save();
  }

  /**
   * Delete pages from a PDF
   */
  static async deletePages(source: ArrayBuffer, pageNumbers: number[]): Promise<Uint8Array> {
    const sourcePdf = await PDFManipulator.loadPdf(source);
    const total = sourcePdf.getPageCount();
    const toDelete = new Set(pageNumbers.map((n) => n - 1));
    const keepIndices = Array.from({ length: total }, (_, i) => i).filter((i) => !toDelete.has(i));

    if (keepIndices.length === 0) {
      throw new Error('Cannot delete all pages of a PDF document');
    }

    try {
      const newPdf = await PDFDocument.create();
      const copiedPages = await newPdf.copyPages(sourcePdf, keepIndices);
      copiedPages.forEach((page) => newPdf.addPage(page));
      return await newPdf.save();
    } catch (err) {
      console.warn('copyPages failed in deletePages, falling back to in-place removePage:', err);
      const pdf = await PDFManipulator.loadPdf(source);
      const sortedIndices = Array.from(toDelete).sort((a, b) => b - a);
      for (const idx of sortedIndices) {
        if (idx >= 0 && idx < pdf.getPageCount()) {
          pdf.removePage(idx);
        }
      }
      return await pdf.save();
    }
  }

  /**
   * Rotate pages in a PDF
   */
  static async rotatePages(
    source: ArrayBuffer,
    pageNumbers: number[],
    angle: number
  ): Promise<Uint8Array> {
    const pdf = await PDFManipulator.loadPdf(source);

    for (const pageNum of pageNumbers) {
      if (pageNum >= 1 && pageNum <= pdf.getPageCount()) {
        const page = pdf.getPage(pageNum - 1);
        const currentRotation = page.getRotation().angle;
        page.setRotation(degrees((currentRotation + angle + 360) % 360));
      }
    }

    return pdf.save();
  }

  /**
   * Reorder pages in a PDF
   */
  static async reorderPages(source: ArrayBuffer, newOrder: number[]): Promise<Uint8Array> {
    const sourcePdf = await PDFManipulator.loadPdf(source);
    const newPdf = await PDFDocument.create();
    const indices = newOrder.map((n) => n - 1).filter((n) => n >= 0 && n < sourcePdf.getPageCount());
    const copiedPages = await newPdf.copyPages(sourcePdf, indices);
    copiedPages.forEach((page) => newPdf.addPage(page));
    return newPdf.save();
  }

  /**
   * Overlay PNG images (e.g. annotations/signatures) onto specific pages
   */
  static async addOverlaysToPages(
    source: ArrayBuffer,
    overlays: { pageNumber: number; imageBytes: ArrayBuffer }[]
  ): Promise<Uint8Array> {
    const pdf = await PDFManipulator.loadPdf(source);
    for (const item of overlays) {
      if (item.pageNumber >= 1 && item.pageNumber <= pdf.getPageCount()) {
        const page = pdf.getPage(item.pageNumber - 1);
        const image = await pdf.embedPng(item.imageBytes.slice(0));
        const { width, height } = page.getSize();
        page.drawImage(image, {
          x: 0,
          y: 0,
          width,
          height,
        });
      }
    }
    return pdf.save();
  }

  /**
   * Add text to a specific page
   */
  static async addTextToPage(
    source: ArrayBuffer,
    pageNumber: number,
    options: AddTextOptions
  ): Promise<Uint8Array> {
    const pdf = await PDFManipulator.loadPdf(source);
    const page = pdf.getPage(pageNumber - 1);
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    const color = options.color || { r: 0, g: 0, b: 0 };

    page.drawText(options.text, {
      x: options.x,
      y: page.getHeight() - options.y - (options.fontSize || 14),
      size: options.fontSize || 14,
      font,
      color: rgb(color.r, color.g, color.b),
    });

    return pdf.save();
  }

  /**
   * Add an image to a specific page
   */
  static async addImageToPage(
    source: ArrayBuffer,
    pageNumber: number,
    options: AddImageOptions
  ): Promise<Uint8Array> {
    const pdf = await PDFManipulator.loadPdf(source);
    const page = pdf.getPage(pageNumber - 1);

    const image =
      options.imageType === 'png'
        ? await pdf.embedPng(options.imageBytes.slice(0))
        : await pdf.embedJpg(options.imageBytes.slice(0));

    page.drawImage(image, {
      x: options.x,
      y: page.getHeight() - options.y - options.height,
      width: options.width,
      height: options.height,
    });

    return pdf.save();
  }

  /**
   * Create a PDF from images
   */
  static async createFromImages(
    images: { data: ArrayBuffer; type: 'png' | 'jpg' }[]
  ): Promise<Uint8Array> {
    const pdf = await PDFDocument.create();

    for (const img of images) {
      const image =
        img.type === 'png'
          ? await pdf.embedPng(img.data.slice(0))
          : await pdf.embedJpg(img.data.slice(0));

      const dims = image.scale(1);
      const page = pdf.addPage([dims.width, dims.height]);
      page.drawImage(image, {
        x: 0,
        y: 0,
        width: dims.width,
        height: dims.height,
      });
    }

    return pdf.save();
  }

  /**
   * Get form fields from a PDF
   */
  static async getFormFields(source: ArrayBuffer) {
    const pdf = await PDFManipulator.loadPdf(source);
    const form = pdf.getForm();
    const fields = form.getFields();

    return fields.map((field) => ({
      name: field.getName(),
      type: field.constructor.name,
    }));
  }

  /**
   * Fill form fields
   */
  static async fillFormFields(
    source: ArrayBuffer,
    fieldValues: Record<string, string>
  ): Promise<Uint8Array> {
    const pdf = await PDFManipulator.loadPdf(source);
    const form = pdf.getForm();

    for (const [name, value] of Object.entries(fieldValues)) {
      try {
        const field = form.getTextField(name);
        field.setText(value);
      } catch {
        console.warn(`Could not fill field: ${name}`);
      }
    }

    return pdf.save();
  }

  /**
   * Add a blank page
   */
  static async addBlankPage(
    source: ArrayBuffer,
    afterPage?: number,
    size: [number, number] = PageSizes.A4
  ): Promise<Uint8Array> {
    const pdf = await PDFManipulator.loadPdf(source);

    if (afterPage !== undefined && afterPage >= 0 && afterPage <= pdf.getPageCount()) {
      pdf.insertPage(afterPage, size);
    } else {
      pdf.addPage(size);
    }

    return pdf.save();
  }

  /**
   * Get document info
   */
  static async getDocumentInfo(source: ArrayBuffer) {
    const pdf = await PDFManipulator.loadPdf(source);
    return {
      pageCount: pdf.getPageCount(),
      title: pdf.getTitle(),
      author: pdf.getAuthor(),
      subject: pdf.getSubject(),
      creator: pdf.getCreator(),
      producer: pdf.getProducer(),
      creationDate: pdf.getCreationDate(),
      modificationDate: pdf.getModificationDate(),
      pages: Array.from({ length: pdf.getPageCount() }, (_, i) => {
        const page = pdf.getPage(i);
        const { width, height } = page.getSize();
        return { number: i + 1, width, height, rotation: page.getRotation().angle };
      }),
    };
  }
}
